import React, { useEffect, useRef, useState } from 'react';
import './settings.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  getFilterData,
  getJsonFromStorage,
  getPaginationOpts, getResponseError,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import Loader from '../../../components/base/Loader/Loader';
import { getSettingsData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import getDefaultDataRange from '../../../utils/getDefaultDataRange';
import { SETTINGS_MODULE } from '../../../configs/moduleNames';
import {
  loadSettingsList, deleteSettings, editSetting, loadSettingsById, createSetting,
} from '../actions';
import { settingsColumns } from './settingsColumns';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Icons, Button } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInput from '../../../components/fields/TextInput/TextInput';
import SettingsPageView from './SettingsPageView';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { showGlobalConfirm } from '../../System/actions';
import SettingsPageForm from './SettingsPageForm';

export default function SettingsPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const {
    settingsList,
    settingsListMeta,
    loadingSettingsList,
    loadingCreateSettings,
    loadingUpdateSettings,
    setting,
  } = useSelector(getSettingsData);

  const filterName = 'settings-filter';
  const [isOpenDetails, setOpenDetails] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { formatMessage } = useIntl();
  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[SETTINGS_MODULE].settingsListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
    };
    const p1 = dispatch(loadSettingsList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const loadSetting = id => {
    if (!id) {
      return;
    }
    dispatch(loadSettingsById(id)).catch(responseLogWithNotification);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  const defaultDate = getDefaultDataRange();
  const defaultFilterValues = {
    created_at_from: defaultDate[0], created_at_to: defaultDate[1], search: '', created_at: '',
  };

  useEffect(() => reset(getFilterData(filterName, defaultFilterValues)), []);
  const {
    getValues, watch, control, reset,
  } = useForm({ defaultValues: getJsonFromStorage('registry-export-filter', defaultFilterValues) });

  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleEdit = (id, body) => {
    if (!id) {
      return;
    }
    const scopes = body?.scopes ? Object.entries(body.scopes)?.filter(item => item[1]).map(key => key[0]) : [];
    dispatch(editSetting({ id, ...body, scopes }))
      .then(() => {
        successNotification('Настройка api', 'Настройки обновлены');
        loadSetting(id);
        loadData();
      }).catch(response => {
        errorNotification('Настройка api', getResponseError(response));
      });
  };

  const handleCreate = body => {
    const scopes = body?.scopes ? Object.entries(body.scopes)?.filter(item => item[1]).map(key => key[0]) : [];
    dispatch(createSetting({ ...body, scopes }))
      .then(() => {
        successNotification('Настройка api', 'Настройки обновлены');
        setShowForm(false);
        loadData();
      }).catch(response => {
        errorNotification('Настройка api', getResponseError(response));
      });
  };

  const handleShow = item => event => {
    dispatch(loadSettingsById(item?.id))
      .then(() => {
        setOpenDetails(true);
      })
      .catch(response => {
        errorNotification('Загрузка настройки', getResponseError(response));
      });
  };

  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: <div className="f2 text-sm-center">Удаление настройки — {`"${item.name}"` || ''}</div>,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteSettings(item.id)).then(() => {
        loadData();
        successNotification('Удаление настройки', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление настройки', getResponseError(response));
      });
    }).catch(() => {});
    event?.preventDefault();
    event?.stopPropagation();
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions settings-more-portal"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={handleShow(item)}>
            Изменить настройки API
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  return (
    <div className="page settings-page">
      <Loader global loading={loadingSettingsList || loadingCreateSettings || loadingUpdateSettings} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.settings' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
            inputBefore={(
              <div className="search-wrapper">
                <Icons customIcon className="search" width={24} height={24} icon="search" />
              </div>
            )}
          />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setShowForm(true)}>
            <Icons customIcon className="file-download" width={24} height={24} icon="plus" />
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={settingsList}
                columns={settingsColumns}
                name="settings-list"
                additionalColumns={[]}
                settingsFormatter={moreFormatter}
              />
            </div>
          </div>
          <Pagination meta={settingsListMeta} list={settingsList} handleChangeMeta={loadData} />
        </div>
      </div>
      {isOpenDetails && setting ? (<SettingsPageView onHide={() => { setOpenDetails(false); }} item={isOpenDetails} handleEdit={handleEdit} />) : null}
      {showForm ? (<SettingsPageForm onHide={() => { setShowForm(false); }} handleCreate={handleCreate} />) : null}
    </div>
  );
}
